import {Memoize} from 'typescript-memoize';
import {Comparator, IsEqual} from '../comparator';
import {type GarfieldReport} from './garfield-report';
import {RouteParser} from '../route-parser';
import {intersection} from '../../services/functions/array/intersection';
import {KeysOfCondition} from 'src/app/services/keys-with-condition.service';

import {ReportKind} from './report-kind';
import {GarfieldFundNode} from '../../components/garfield-funds-selector/garfield-fund-node-model';
import {GarfieldPivotDisplayUnit} from '../pivot-table/garfield-pivot-display-unit';
import {GovTypeCode} from '../../services/api/fit-api/models/gov-type-code';
import {ALL_COUNTIES, AllCounties} from '../../components/counties-selector/counties-model';
import {type GarfieldOptionsBundle} from './garfield-options-bundle';
import {type GarfieldGovernmentList} from '../government-list/garfield-government-list';
import {ALL_GOVT_TYPES, GovernmentType} from '../../services/api/fit-api/models/government-type';
import {County} from '../../services/api/fit-api/models/snapshots/county';
import {
	AccountingBasis,
	ALL_ACCOUNTING_BASES,
	AllAccountingBases
} from '../../services/api/fit-api/models/accounting-basis';

export abstract class Option<Values = any, Value = any> {
	abstract routeParameterKey: KeysOfCondition<RouteParser, Value | undefined>;

	// All values possible for the option, in most cases, the data source of the corresponding widget itself
	protected abstract _values: () => Values;
	@Memoize()
	get values(): Values {
		return this._values();
	}

	// Provide implementation specific default value getter
	protected abstract _defaultValue: () => Value;
	@Memoize()
	get defaultValue(): Value {
		return this._defaultValue();
	}

	@Memoize()
	get appliedValue(): Value {
		if (this.optionsBundle.route) {
			return new RouteParser(this.optionsBundle.route)[this.routeParameterKey] as Value ?? this.defaultValue;
		}

		return this.defaultValue;
	}

	// default to "by reference"
	readonly comparator: IsEqual<Value> = Comparator.reference;

	constructor(
		public readonly optionsBundle: GarfieldOptionsBundle
	) {
	}

	/**
	 * Is this the default value?
	 * @param value If not provided, tests against appliedValue
	 */
	isDefaultValue = (value = this.appliedValue): boolean =>
		this.comparator(value, this.defaultValue);
}

// region Report Options
// /**
//  * Multi Year Slider
//  */
// // Tuple representing the beginning and end of a range of integers [start, end]
// // Note: This can be a little tricky to work with, so we may consider making it a class to enforce behavior
export type BoundedRange = [number, number];
//
// export class MultiYearReportOption extends ReportOption<
// 	// This enforces types and provides type info in the HTML template
// 	Set<number>, BoundedRange
// > {
// 	override routeParameterKey = 'years';
//
// 	protected _values = () =>
// 		new Set(this.report.datasetsDetail.map(x => x.detail.includedYears).flat());
//
// 	protected _defaultValue = () =>
// 		[
// 			Array.from(this.values).slice(-4, -3)[0],
// 			Array.from(this.values).slice(-1)[0]
// 		] satisfies BoundedRange;
//
// 	override comparator = Comparator.entries;
// }

/**
 * Single Year Slider
 */
export class SingleYearReportOption extends Option<
	Set<number>, number
> {
	routeParameterKey = 'displayYear' as const;

	// union of all years (sorted)
	_values = () => new Set((this.optionsBundle as GarfieldReport).datasetsDetail.map(x => x.detail.includedYears).flat().sort((a, b) => a - b));

	// default value is the latest year the resolved datasets have in common
	_intersectedYears = (this.optionsBundle as GarfieldReport).datasetsDetail.length > 0 ? intersection((this.optionsBundle as GarfieldReport).datasetsDetail.map(x => x.detail.includedYears)) : [];
	_defaultValue = () => Math.max(...this._intersectedYears);
}
/**
 * Fund Selector
 */
export class SelectedFundOption extends Option<
	GarfieldFundNode[], GarfieldFundNode
> {
	routeParameterKey = 'fundNode' as const;
	_values = () => (this.optionsBundle as GarfieldReport).filedFundNodes ?? [];
	override comparator = Comparator.entries;
	_defaultValue = () => {
		if (!this.values) {
			throw new Error('No filed funds resolved');
		}
		return this.getDefaultFundNode(this.values);
	}

	/**
	 * Get default fund value based on filed funds/fund types
	 * @param filedFundNodes
	 */
	// todo add default at fund level when retrofitting financial report
	getDefaultFundNode = (filedFundNodes: GarfieldFundNode[]) => {
		let defaultSelectedFundNode = new GarfieldFundNode('All funds', null, null);

		const governmentFundNode = filedFundNodes.find(f => f.fundCategoryId === 1); // has governmental categories
		const proprietaryFundNode = filedFundNodes.find(f => f.fundCategoryId === 2); // has proprietary categories

		// All filed funds are Governmental. Then, the default is "Governmental".
		if (governmentFundNode && !proprietaryFundNode) {
			defaultSelectedFundNode = new GarfieldFundNode(1, null, null);
		}

		// or all funds are Proprietary (not counting Internal Service funds). Then, the default is "Enterprise" (fundTypeId = 4).
		if (proprietaryFundNode && !governmentFundNode) {
			defaultSelectedFundNode = new GarfieldFundNode(2, 4, null);
		}

		return defaultSelectedFundNode;
	}
}

export class ExcludeInternalServiceFundsOption extends Option<
	Set<boolean>, boolean
> {
	routeParameterKey = 'excludeInternalServiceFunds' as const;
	_values = () => new Set([true, false]);
	_defaultValue = () => true;
	override comparator = Comparator.primitive;
}

/**
 * Display Options
 */
export class DisplayUnitDisplayOption extends Option<
	Set<GarfieldPivotDisplayUnit>, GarfieldPivotDisplayUnit
> {
	override get appliedValue() {
		// if summary report and applied value is % of total return to default
		if ((this.optionsBundle as GarfieldReport).kind === ReportKind.summary && super.appliedValue === GarfieldPivotDisplayUnit.percentOfTotal) {
			return this.defaultValue;
		}

		return super.appliedValue;
	}

	routeParameterKey = 'displayUnit' as const;
	protected _values = () => new Set(Object.values(GarfieldPivotDisplayUnit));
	protected _defaultValue = () => GarfieldPivotDisplayUnit.dollars;
}

export class ShowAccountNumbersDisplayOption extends Option<
	Set<boolean>, boolean
> {
	routeParameterKey = 'showAccountNumbers' as const;
	protected _values = () => new Set([true, false]);
	protected _defaultValue = () => false;
	override comparator = Comparator.primitive;
}

export class ByExpenditureObjectsDisplayOption extends Option<
	Set<boolean>, boolean
> {
	routeParameterKey = 'byExpenditureObjects' as const;
	_values = () => new Set([true, false]);
	_defaultValue = () => false;
	override comparator = Comparator.primitive;
}
//endregion

//region Government Page Options
/**
 * Filter By Name Option
 */
export class FilterByNameOption extends Option<
	undefined, string
> {
	routeParameterKey = 'filterByName' as const;
	_values = () => undefined;
	_defaultValue = () => '';
	override comparator = Comparator.primitive;
}
/**
 * Government Type Selector
 */
export class GovernmentTypeOption extends Option<
	GovernmentType[], (ALL_GOVT_TYPES | GovTypeCode)
> {
	routeParameterKey = 'govTypeCode' as const;
	_values = () => (this.optionsBundle as GarfieldGovernmentList).governmentTypes ?? [];
	_defaultValue = () => ALL_GOVT_TYPES;
	// default "by reference" comparator because we are just testing 'all' for default value
}
export class ShowLocalGovernmentsOnlyOption extends Option<
	Set<boolean>, boolean
> {
	routeParameterKey = 'showLocalGovernmentsOnly' as const;
	_values = () => new Set([true, false]);
	_defaultValue = () => true;
	override comparator = Comparator.primitive;
}
/**
 * Counties Selector
 *
 * Note: value reflects 'all' and array of county codes (number[])
 */
export class CountiesOption extends Option<
	County[], AllCounties | number[]
> {
	routeParameterKey = 'counties' as const;
	_values = () => (this.optionsBundle as GarfieldGovernmentList).counties ?? [];
	_defaultValue = (): AllCounties => ALL_COUNTIES;
	// default "by reference" comparator because we are just testing 'all' for default value
}
/**
 * Accounting Bases Selector
 *
 * Note: value reflects 'all' and array of accounting basis id (number[])
 */
export class AccountingBasesOption extends Option<
	AccountingBasis[], AllAccountingBases | number[]
> {
	routeParameterKey = 'accountingBases' as const;
	_values = () => (this.optionsBundle as GarfieldGovernmentList).accountingBases ?? [];
	_defaultValue = (): AllAccountingBases => ALL_ACCOUNTING_BASES;
	// default "by reference" comparator because we are just testing 'all' for default value
}
//endregion






